import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { db } from "@/api/db";
import { Search, Users, Check, X, ChevronLeft, ChevronRight, Plus, CreditCard, XCircle, Compass, UserCircle2, ExternalLink, Mic, Clock, UserCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function SubscriptionBadge({ status }) {
  if (status === "active") {
    return <Badge className="text-xs border-0 bg-emerald-100 text-emerald-700">Ativa</Badge>;
  }
  if (status === "canceled") {
    return <Badge className="text-xs border-0 bg-red-100 text-red-700">Cancelada</Badge>;
  }
  return <Badge className="text-xs border-0 bg-gray-100 text-gray-500">Sem assinatura</Badge>;
}

function TestBadge({ completed }) {
  if (completed) {
    return (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100">
        <Check className="w-3.5 h-3.5 text-emerald-600" />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100">
      <X className="w-3.5 h-3.5 text-gray-400" />
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
          {total > 0 && total !== value && (
            <p className="text-[10px] text-gray-400">{pct}% do total</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminUsers() {
  const { user: authUser, isLoadingAuth } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  const [testFilter, setTestFilter] = useState("all");
  const [stats, setStats] = useState(null);

  // Create user modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ full_name: "", email: "", cpf: "" });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [lookupResult, setLookupResult] = useState(null); // { found, user, hasProfile }

  // User detail modal
  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedInterview, setExpandedInterview] = useState(null);

  const loadUsers = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const data = await db.admin.users({
        page: params.page || 1,
        search: params.search || undefined,
        subscription: params.subscription !== "all" ? params.subscription : undefined,
        test: params.test !== "all" ? params.test : undefined,
      });
      setUsers(data.users);
      setTotal(data.total);
      setTotalPages(data.totalPages);
      setPage(data.page);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isLoadingAuth) return;
    if (authUser?.role !== "admin") {
      navigate("/", { replace: true });
      return;
    }
    loadUsers({ page: 1, search, subscription: subscriptionFilter, test: testFilter });
    db.admin.stats().then(setStats).catch(() => {});
  }, [authUser, isLoadingAuth]);

  function handleSearch(e) {
    e.preventDefault();
    setSearch(searchInput);
    loadUsers({ page: 1, search: searchInput, subscription: subscriptionFilter, test: testFilter });
  }

  function handleSubscriptionChange(value) {
    setSubscriptionFilter(value);
    loadUsers({ page: 1, search, subscription: value, test: testFilter });
  }

  function handleTestChange(value) {
    setTestFilter(value);
    loadUsers({ page: 1, search, subscription: subscriptionFilter, test: value });
  }

  function goToPage(p) {
    loadUsers({ page: p, search, subscription: subscriptionFilter, test: testFilter });
  }

  function openCreate() {
    setCreateForm({ full_name: "", email: "", cpf: "" });
    setCreateError("");
    setLookupResult(null);
    setShowCreate(true);
  }

  async function handleEmailLookup(email) {
    if (!email || !email.includes("@")) {
      setLookupResult(null);
      return;
    }
    try {
      const result = await db.admin.lookupEmail(email);
      setLookupResult(result);
      if (result.found) {
        setCreateForm(f => ({
          ...f,
          full_name: result.user.full_name || f.full_name,
          cpf: result.user.cpf || f.cpf,
        }));
      }
    } catch {
      setLookupResult(null);
    }
  }

  async function handleCreate() {
    setCreating(true);
    setCreateError("");
    try {
      const result = await db.admin.createUser({
        email: createForm.email,
        full_name: createForm.full_name,
        cpf: createForm.cpf || undefined,
      });
      setShowCreate(false);
      loadUsers({ page: 1, search, subscription: subscriptionFilter, test: testFilter });
      db.admin.stats().then(setStats).catch(() => {});
    } catch (err) {
      setCreateError(err.message);
    }
    setCreating(false);
  }

  async function openDetail(userId) {
    setDetail(null);
    setExpandedInterview(null);
    setShowDetail(true);
    setDetailLoading(true);
    try {
      const data = await db.admin.userDetail(userId);
      setDetail(data);
    } catch (err) {
      console.error("Failed to load user detail:", err);
    }
    setDetailLoading(false);
  }

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          <p className="text-gray-500 mt-1">{total} usuário(s) Talent Pro</p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-500 hover:bg-emerald-600 gap-2">
          <Plus className="w-4 h-4" /> Novo Usuário
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <StatCard icon={Users} label="Total Talent Pro" value={stats.total_users} color="#008572" total={0} />
          <StatCard icon={CreditCard} label="Assinaturas ativas" value={stats.active_subscriptions} color="#00DCA4" total={stats.total_users} />
          <StatCard icon={XCircle} label="Canceladas" value={stats.canceled_subscriptions} color="#EF4444" total={stats.total_users} />
          <StatCard icon={UserCheck} label="Perfis completos" value={stats.profiles_completed} color="#3B82F6" total={stats.total_users} />
          <StatCard icon={Compass} label="Vocacional feito" value={stats.vocational_done} color="#6366F1" total={stats.total_users} />
          <StatCard icon={UserCircle2} label="Portfólios publicados" value={stats.portfolios_published} color="#F59E0B" total={stats.total_users} />
        </div>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </form>
            <Select value={subscriptionFilter} onValueChange={handleSubscriptionChange}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Assinatura" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas assinaturas</SelectItem>
                <SelectItem value="active">Ativa</SelectItem>
                <SelectItem value="canceled">Cancelada</SelectItem>
                <SelectItem value="none">Sem assinatura</SelectItem>
              </SelectContent>
            </Select>
            <Select value={testFilter} onValueChange={handleTestChange}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Testes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos testes</SelectItem>
                <SelectItem value="vocational">Vocacional feito</SelectItem>
                <SelectItem value="disc">DISC feito</SelectItem>
                <SelectItem value="saboteur">Sabotadores feito</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {users.length === 0 && !loading ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-16 text-center text-gray-400">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Nenhum usuário encontrado.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Card className={`border-0 shadow-sm overflow-hidden ${loading ? "opacity-60" : ""}`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b" style={{ background: "#F9FAFA" }}>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Nome</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Email</th>
                      <th className="text-center px-4 py-3 font-semibold text-gray-600">Assinatura</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-600">Vocacional</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-600">DISC</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-600">Sabotadores</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-600">Perfil</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-600">Entrevistas</th>
                      <th className="text-center px-3 py-3 font-semibold text-gray-600">Portfólio</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">Cadastro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => openDetail(user.id)}>
                        <td className="px-4 py-3 font-medium text-gray-900">{user.full_name || "—"}</td>
                        <td className="px-4 py-3 text-gray-600">{user.email}</td>
                        <td className="px-4 py-3 text-center">
                          <SubscriptionBadge status={user.subscription_status} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <TestBadge completed={user.vocational_completed} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <TestBadge completed={user.disc_completed} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <TestBadge completed={user.saboteur_completed} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Badge className={`text-xs border-0 ${user.profile_completed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                            {user.profile_completed ? "Completo" : "Incompleto"}
                          </Badge>
                        </td>
                        <td className="px-3 py-3 text-center font-medium text-gray-700">
                          {user.interview_count > 0 ? user.interview_count : "—"}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <TestBadge completed={user.has_portfolio} />
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">
                          {new Date(user.created_at).toLocaleDateString("pt-BR")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Mobile cards */}
          <div className={`md:hidden space-y-3 ${loading ? "opacity-60" : ""}`}>
            {users.map(user => (
              <Card key={user.id} className="border-0 shadow-sm cursor-pointer" onClick={() => openDetail(user.id)}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{user.full_name || "—"}</p>
                      <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Badge className={`text-xs border-0 ${user.profile_completed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {user.profile_completed ? "Completo" : "Incompleto"}
                      </Badge>
                      <SubscriptionBadge status={user.subscription_status} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <span>Vocacional</span>
                      <TestBadge completed={user.vocational_completed} />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span>DISC</span>
                      <TestBadge completed={user.disc_completed} />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span>Sabotadores</span>
                      <TestBadge completed={user.saboteur_completed} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <span>Entrevistas: {user.interview_count > 0 ? user.interview_count : "—"}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span>Portfólio</span>
                      <TestBadge completed={user.has_portfolio} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{new Date(user.created_at).toLocaleDateString("pt-BR")}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Página {page} de {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="h-8 gap-1"
                >
                  <ChevronLeft className="w-4 h-4" /> Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="h-8 gap-1"
                >
                  Próxima <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* User detail dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailLoading || !detail ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {detail.user.full_name || "Usuário"}
                  <SubscriptionBadge status={detail.user.subscription_status} />
                </DialogTitle>
                <p className="text-sm text-gray-500">{detail.user.email}{detail.user.cpf ? ` — CPF: ${detail.user.cpf}` : ""}</p>
                <p className="text-xs text-gray-400">Cadastro: {new Date(detail.user.created_at).toLocaleDateString("pt-BR")}</p>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Portfolio */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Portfólio</h3>
                  {detail.portfolio ? (
                    <Card className="border shadow-none">
                      <CardContent className="p-3 space-y-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{detail.portfolio.headline || detail.portfolio.profession || "Sem título"}</p>
                            <Badge className={`text-xs border-0 mt-1 ${detail.portfolio.is_published ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                              {detail.portfolio.is_published ? "Publicado" : "Rascunho"}
                            </Badge>
                          </div>
                          {detail.portfolio.is_published && detail.portfolio.slug && (
                            <a href={`/${detail.portfolio.slug}`} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 flex items-center gap-1 text-sm">
                              Ver <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <p className="text-sm text-gray-400">Nenhum portfólio criado.</p>
                  )}
                </section>

                {/* Vocacional */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Teste Vocacional</h3>
                  {detail.user.vocational_completed ? (
                    <Card className="border shadow-none">
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">Resultado: {detail.user.vocational_result || "—"}</p>
                          {detail.user.vocational_completed_at && (
                            <span className="text-xs text-gray-400">{new Date(detail.user.vocational_completed_at).toLocaleDateString("pt-BR")}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <p className="text-sm text-gray-400">Não realizou o teste.</p>
                  )}
                </section>

                {/* DISC */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Teste DISC</h3>
                  {detail.user.disc_completed ? (
                    <Card className="border shadow-none">
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">Perfil: {detail.user.disc_result || "—"}</p>
                          {detail.user.disc_completed_at && (
                            <span className="text-xs text-gray-400">{new Date(detail.user.disc_completed_at).toLocaleDateString("pt-BR")}</span>
                          )}
                        </div>
                        {detail.user.disc_scores && (
                          <div className="flex gap-3 mt-1">
                            {Object.entries(detail.user.disc_scores).map(([key, val]) => (
                              <div key={key} className="text-center">
                                <p className="text-lg font-bold" style={{ color: "#008572" }}>{val}%</p>
                                <p className="text-xs text-gray-500 font-semibold">{key}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <p className="text-sm text-gray-400">Não realizou o teste.</p>
                  )}
                </section>

                {/* Sabotadores */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Teste Sabotadores</h3>
                  {detail.user.saboteur_completed ? (
                    <Card className="border shadow-none">
                      <CardContent className="p-3">
                        <p className="text-sm font-medium text-gray-900">Sabotador principal: {detail.user.saboteur_result || "—"}</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <p className="text-sm text-gray-400">Não realizou o teste.</p>
                  )}
                </section>

                {/* Entrevistas */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    Entrevistas simuladas ({detail.interviews.length})
                  </h3>
                  {detail.interviews.length === 0 ? (
                    <p className="text-sm text-gray-400">Nenhuma entrevista realizada.</p>
                  ) : (
                    <div className="space-y-2">
                      {detail.interviews.map(iv => (
                        <Card key={iv.id} className="border shadow-none">
                          <CardContent className="p-3">
                            <div
                              className="flex items-center justify-between cursor-pointer"
                              onClick={() => setExpandedInterview(expandedInterview === iv.id ? null : iv.id)}
                            >
                              <div className="flex items-center gap-2">
                                <Mic className="w-4 h-4 text-gray-400" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{iv.job_title || "Entrevista"}</p>
                                  <p className="text-xs text-gray-500">
                                    {iv.interview_type}{iv.seniority ? ` — ${iv.seniority}` : ""}
                                    {iv.duration_seconds ? ` — ${Math.round(iv.duration_seconds / 60)} min` : ""}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {iv.analysis?.overall_score != null && (
                                  <Badge className="text-xs border-0 bg-emerald-100 text-emerald-700">
                                    Score: {iv.analysis.overall_score}
                                  </Badge>
                                )}
                                <Badge className={`text-xs border-0 ${iv.status === "done" ? "bg-emerald-100 text-emerald-700" : "bg-yellow-100 text-yellow-700"}`}>
                                  {iv.status === "done" ? "Concluída" : iv.status === "analyzing" ? "Analisando" : "Em andamento"}
                                </Badge>
                                <span className="text-xs text-gray-400">{new Date(iv.created_at).toLocaleDateString("pt-BR")}</span>
                              </div>
                            </div>

                            {expandedInterview === iv.id && (
                              <div className="mt-3 pt-3 border-t space-y-3">
                                {/* Analysis scorecard */}
                                {iv.analysis?.scorecard && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600 mb-1">Scorecard</p>
                                    <div className="space-y-1">
                                      {iv.analysis.scorecard.map((item, i) => (
                                        <div key={i} className="flex items-center justify-between text-xs">
                                          <span className="text-gray-700">{item.dimension}</span>
                                          <div className="flex items-center gap-2">
                                            <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                              <div className="h-full rounded-full" style={{ width: `${item.score}%`, background: item.score >= 70 ? "#00DCA4" : item.score >= 40 ? "#F59E0B" : "#EF4444" }} />
                                            </div>
                                            <span className="font-semibold text-gray-600 w-8 text-right">{item.score}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Weaknesses */}
                                {iv.analysis?.weaknesses?.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600 mb-1">Pontos fracos</p>
                                    <ul className="text-xs text-gray-600 space-y-0.5">
                                      {iv.analysis.weaknesses.map((w, i) => (
                                        <li key={i}>- {w}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {/* Transcript */}
                                {iv.transcript?.length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600 mb-1">Transcrição</p>
                                    <div className="max-h-60 overflow-y-auto space-y-2 text-xs">
                                      {iv.transcript.map((msg, i) => (
                                        <div key={i} className={`p-2 rounded-lg ${msg.role === "interviewer" ? "bg-gray-50 text-gray-700" : "bg-emerald-50 text-emerald-900"}`}>
                                          <p className="font-semibold text-[10px] uppercase tracking-wide mb-0.5" style={{ color: msg.role === "interviewer" ? "#99A6AB" : "#008572" }}>
                                            {msg.role === "interviewer" ? "Entrevistador" : "Candidato"}
                                          </p>
                                          <p>{msg.text}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create user dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Usuário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Email *</label>
              <Input
                type="email"
                value={createForm.email}
                onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                onBlur={e => handleEmailLookup(e.target.value)}
                placeholder="joao@email.com"
                className="mt-1"
              />
              {lookupResult?.found && (
                <p className="text-xs mt-1 text-emerald-600">
                  Conta encontrada na plataforma.
                  {lookupResult.hasProfile ? " Já possui perfil Talent Pro." : " Será adicionado ao Talent Pro."}
                </p>
              )}
              {lookupResult && !lookupResult.found && createForm.email.includes("@") && (
                <p className="text-xs mt-1 text-amber-600">
                  Conta nova — receberá email para definir senha.
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Nome completo *</label>
              <Input
                value={createForm.full_name}
                onChange={e => setCreateForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Ex: João Silva"
                className="mt-1"
                disabled={lookupResult?.found}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">CPF</label>
              <Input
                value={createForm.cpf}
                onChange={e => setCreateForm(f => ({ ...f, cpf: e.target.value }))}
                placeholder="000.000.000-00"
                className="mt-1"
                disabled={lookupResult?.found && lookupResult.user.cpf}
              />
            </div>
            {createError && (
              <p className="text-sm text-red-600">{createError}</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1">Cancelar</Button>
              <Button
                onClick={handleCreate}
                disabled={!createForm.full_name || !createForm.email || creating}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              >
                {creating ? "Adicionando..." : lookupResult?.found ? "Adicionar ao Talent Pro" : "Criar Usuário"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
